import { useEffect, useRef, useState } from 'react';

import { PageHeader } from '@/components/app-shell/PageHeader';
import { useToast } from '@/features/campaigns/toast-context';

import { patchTenantConfig, patchTenantIdp, putRoleMappings } from './admin-api';
import { formatBatch, formatStat } from './admin-format';
import { ManageAccountPage } from './ManageAccountPage';
import { OnboardingWizard } from './OnboardingWizard';
import { useCreateTenant, useTenants } from './useTenants';
import type { TenantCard } from './admin-api';
import type { OnboardingResult } from './OnboardingWizard';

// v7 account-card status pill (buildAccountCard): the prototype uses the
// --success/--success-dim (and --danger/--danger-dim) design tokens directly, NOT
// a Tailwind opacity utility — bg-success/10 computes a different rgba path than the
// prototype's --success-dim (rgba(16,185,129,0.12)). Reference the CSS vars so the
// computed color matches the served prototype exactly (SCRUM-432).
const STATUS_CLASSES: Record<TenantCard['status'], string> = {
  active: 'text-[var(--success)] bg-[var(--success-dim)]',
  inactive: 'text-text-3 bg-bg-2',
  closed: 'text-[var(--danger)] bg-[var(--danger-dim)]',
};

/**
 * The Platform Admin "Customers" console (SCRUM-391, v7 renderCustomerDashboard):
 * BankIQ's customer banks as account cards — count header, expandable search,
 * New Customer wizard, and per-card Manage account navigation. Backed by the
 * real /api/admin/tenants surface (SCRUM-390).
 */
export function CustomersPage() {
  const { showToast } = useToast();
  const { tenants, isLoading, isError } = useTenants();
  const createMutation = useCreateTenant();

  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [wizardOpen, setWizardOpen] = useState(false);
  const [manageId, setManageId] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  const activeCount = tenants.filter((t) => t.status === 'active').length;
  const query = search.toLowerCase().trim();
  const filtered = query
    ? tenants.filter(
        (t) =>
          t.bankName.toLowerCase().includes(query) ||
          (t.region ?? '').toLowerCase().includes(query) ||
          (t.charter ?? '').toLowerCase().includes(query),
      )
    : tenants;

  const managed = manageId ? tenants.find((t) => t.id === manageId) : null;
  if (managed) {
    return (
      <ManageAccountPage
        tenant={managed}
        onBack={() => {
          setManageId(null);
        }}
      />
    );
  }

  const commitWizard = async (result: OnboardingResult) => {
    const id = await createMutation.mutateAsync({
      bankName: result.bankName,
      charter: result.charter,
      region: result.region,
      primaryContactName: result.primaryContactName,
      primaryContactEmail: result.primaryContactEmail,
      config: result.config,
    });
    // SCRUM-585: the SSO & Access step lands on real aggregate fields via the
    // dedicated admin endpoints. Any failure here surfaces in the wizard (the
    // tenant already exists as a Draft — the admin finishes in Manage Account).
    if (result.idp) {
      await patchTenantIdp(id, result.idp);
    }
    if (result.emailDomains.length > 0) {
      await patchTenantConfig(id, { emailDomains: result.emailDomains });
    }
    if (result.roleMappings.length > 0) {
      await putRoleMappings(id, result.roleMappings);
    }
    setWizardOpen(false);
    showToast({
      title: 'Customer saved',
      body: 'Activate the account from Manage Account when ready to enable sign-in.',
      variant: 'success',
    });
  };

  return (
    <div className="flex min-h-full flex-col" data-testid="admin-customers-page">
      <PageHeader
        title="Customers"
        subtitle="BankIQ customer banks — onboarding, profiles & per-bank setup"
      />

      <div className="p-4">
        {/* Toolbar: count + search + New Customer */}
        <div className="mb-4 flex items-start justify-between">
          <div className="text-text-2 font-mono text-xs">
            {String(tenants.length)} accounts · {String(activeCount)} active
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center">
              <button
                type="button"
                aria-label="Search accounts"
                aria-expanded={searchOpen}
                onClick={() => {
                  setSearchOpen((open) => {
                    if (open) setSearch('');
                    return !open;
                  });
                }}
                className={`border-border-1 bg-bg-3 rounded-tk-md grid h-[38px] w-[38px] place-items-center border transition-colors ${
                  searchOpen
                    ? 'border-brand-primary text-brand-primary'
                    : 'text-text-2 hover:border-brand-primary hover:text-brand-primary'
                }`}
              >
                <svg
                  viewBox="0 0 16 16"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <circle cx="7" cy="7" r="4" />
                  <line x1="10" y1="10" x2="13.5" y2="13.5" />
                </svg>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${searchOpen ? 'w-[232px] opacity-100' : 'w-0 opacity-0'}`}
              >
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search accounts…"
                  aria-label="Search accounts input"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                  }}
                  className="border-border-1 bg-bg-3 text-text-0 rounded-tk-md focus:border-brand-primary ml-2 h-[38px] w-[220px] border px-3 text-[0.8125rem] outline-none"
                />
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                setWizardOpen(true);
              }}
              className="bg-brand-primary text-bg-0 rounded-tk-md flex items-center gap-1.5 px-4 py-2 text-[0.8125rem] font-semibold"
            >
              <svg
                viewBox="0 0 16 16"
                width="14"
                height="14"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <line x1="8" y1="3" x2="8" y2="13" />
                <line x1="3" y1="8" x2="13" y2="8" />
              </svg>
              New Customer
            </button>
          </div>
        </div>

        {/* Account grid */}
        {isLoading ? (
          <div className="text-text-3 py-16 text-center text-sm">Loading accounts…</div>
        ) : isError ? (
          <div className="text-text-3 py-16 text-center text-sm">
            The admin console is unreachable right now — try again shortly.
          </div>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-4">
            {filtered.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => {
                  setManageId(t.id);
                }}
                aria-label={`Manage ${t.bankName}`}
                // SCRUM-1012: stable, seed-invariant hook so a specific tenant is
                // addressable when two share a display name (demo + synthetic "Meridian
                // Community Bank"). The aria-label is no longer unique; the screenshot
                // harness targets [data-tenant-appid="e2e-synthetic-app-id"].
                data-tenant-appid={t.appIdTenantId}
                className="bg-bg-4 border-border-0 rounded-tk-lg hover:border-brand-primary hover:shadow-tk-sm group flex h-full cursor-pointer flex-col border p-6 text-left transition-all hover:-translate-y-px"
              >
                <div className="mb-3 flex items-start justify-between gap-2">
                  <span className="font-display text-text-0 text-[0.9375rem] font-semibold">
                    {t.bankName}
                  </span>
                  <span
                    className={`rounded-full px-2 py-0.5 font-mono text-[0.625rem] font-bold tracking-[0.04em] uppercase ${STATUS_CLASSES[t.status]}`}
                  >
                    {t.status}
                  </span>
                </div>
                <dl className="mb-3 flex flex-col gap-1">
                  <MetaRow label="Charter" value={t.charter ?? '—'} />
                  <MetaRow label="Region" value={t.region ?? '—'} />
                  <MetaRow label="Last Batch" value={formatBatch(t.lastBatchAt)} />
                </dl>
                <div className="border-border-0 mb-3 grid grid-cols-3 gap-2 border-t pt-3">
                  <Stat value={formatStat(t.users)} label="Users" />
                  <Stat value={formatStat(t.insights)} label="Insights" />
                  <Stat value={formatStat(t.campaigns)} label="Campaigns" />
                </div>
                {/* mt-auto pushes the row to the card's bottom; with the grid stretching
                  cards to equal height per row, the "Manage account" rows line up
                  horizontally across cards regardless of meta/stat height (v7 parity +
                  the requested bottom-align). justify-end right-aligns within the card. */}
                <span className="text-text-3 group-hover:text-brand-primary mt-auto flex items-center justify-end gap-1 pt-2 text-xs transition-colors">
                  Manage account
                  <svg
                    viewBox="0 0 16 16"
                    width="12"
                    height="12"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    aria-hidden="true"
                  >
                    <line x1="3" y1="8" x2="13" y2="8" />
                    <polyline points="9 4 13 8 9 12" />
                  </svg>
                </span>
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="text-text-3 col-span-full py-16 text-center text-sm">
                No matching accounts.
              </div>
            )}
          </div>
        )}

        {wizardOpen && (
          <OnboardingWizard
            onClose={() => {
              setWizardOpen(false);
            }}
            onComplete={commitWizard}
          />
        )}
      </div>
    </div>
  );
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-text-3 text-xs">{label}</dt>
      <dd className="text-text-1 font-mono text-[0.6875rem]">{value}</dd>
    </div>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div className="text-center">
      <div className="font-display text-text-0 text-lg font-bold">{value}</div>
      <div className="text-text-3 font-mono text-[0.625rem] tracking-wide uppercase">{label}</div>
    </div>
  );
}
